import { inventoryRowList } from '../compile/inventory-raw-readers';
import { calculateInventoryWeight, sumInventoryDescendantWeight, inventoryLineCostGp, sumInventoryDescendantCostGp } from '../compile/inventory-totals';
import type { InventoryTotalsReaders } from '../compile/inventory-totals';
import type { ItemFieldRow } from '../compile/item-field-reader';

export type InventoryDisplayRow = Record<string, unknown>;
export type InventoryDisplayNode = { name: string; weight: number; gp: number; children: InventoryDisplayNode[]; id?: string; container?: boolean };
export interface InventoryDisplayReaders extends InventoryTotalsReaders {
    inventoryField(row: unknown, key: string): unknown;
    itemField(row: unknown, key: string): unknown;
    statusHas(row: unknown, id: string): boolean;
}
type PageReaders = {
    ftComputedValue(row: unknown, bucket: string, key: string): unknown;
    ftLayerValue(row: unknown, key: string): unknown;
    ftLayerHas(row: unknown, key: string): boolean;
    ftInventoryField(row: unknown, key: string): unknown;
    ftItemsCol(row: unknown, key: string): unknown;
    ftCreatureStatuses(row: unknown, role: string): unknown[];
};

/** The display uses the page's canonical readers and the native inventory compiler's math. */
export function inventoryDisplayReaders(): InventoryDisplayReaders {
    const page = globalThis as unknown as PageReaders;
    return {
        computedValue: (row, bucket, key) => page.ftComputedValue(row, bucket, key) ?? null,
        layerValue: (row, key) => page.ftLayerValue(row, key) ?? null,
        hasLayer: (row, key) => key === 'inventory_in_container' ? inventoryContents(row).length > 0 : page.ftLayerHas(row, key),
        inventoryField: (row, key) => page.ftInventoryField(row, key),
        itemField: (row, key) => page.ftItemsCol(row, key),
        statusHas: (row, id) => page.ftCreatureStatuses(row, 'target').some(status => String(page.ftItemsCol(status, 'item_id') ?? '').toUpperCase() === id),
    };
}

export const inventoryDisplayRows = inventoryRowList;
export function inventoryContents(row: unknown): InventoryDisplayRow[] {
    return inventoryRowList((row as InventoryDisplayRow | null)?.inventory_in_container);
}
export function inventoryEquippedSlot(row: unknown, read = inventoryDisplayReaders()): string {
    const slot = String(read.inventoryField(row, 'equipped') ?? '').trim();
    return /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(slot) ? slot : '';
}
export function isInventoryDisplayContainer(row: ItemFieldRow, read = inventoryDisplayReaders()): boolean {
    return read.itemField(row, 'item_type') === 'container'
        || ['carrying_capacity_cu_ft', 'carrying_capacity_cu_in', 'carrying_capacity_people', 'carrying_capacity_pounds',
            'carrying_capacity_fixed_weight', 'carrying_capacity_gallons', 'carrying_capacity_ounces', 'carrying_capacity_pints']
            .some(key => read.hasLayer(row, key));
}
export function isIntegratedInventory(row: ItemFieldRow, read = inventoryDisplayReaders()): boolean {
    return read.itemField(row, 'item_type') === 'monster_bit' && read.statusHas(row, '36C061BF-E480-4334-961C-26500150E3C4');
}
export function inventoryOnHandRows(rows: readonly InventoryDisplayRow[], creatureId: string, read = inventoryDisplayReaders()): InventoryDisplayRow[] {
    if (!creatureId) return [];
    return rows.filter(row => inventoryEquippedSlot(row, read) === ''
        && Number(read.inventoryField(row, 'inventory_private') ?? 0) !== 2
        && String(read.inventoryField(row, 'possess_id') ?? '').toUpperCase() === creatureId.toUpperCase()
        && !isIntegratedInventory(row, read));
}
export function inventoryRowTotals(row: InventoryDisplayRow, read = inventoryDisplayReaders()) {
    if (row._inventory_refusal || row._entity_refusal) throw new Error(String(row._inventory_refusal ?? row._entity_refusal));
    const children = inventoryContents(row);
    const fixedWeight = read.hasLayer(row, 'carrying_capacity_fixed_weight');
    const ownLb = Number(read.computedValue(row, 'weight', 'total') ?? 0);
    const heldLb = fixedWeight ? sumInventoryDescendantWeight(children, read) : calculateInventoryWeight(children, read);
    const lineGp = inventoryLineCostGp(row, read);
    const contentsGp = sumInventoryDescendantCostGp(children, read);
    const rawCapacity = read.layerValue(row, 'carrying_capacity_pounds');
    const capacityLb = rawCapacity == null || rawCapacity === '' || !Number.isFinite(Number(rawCapacity)) ? null : Number(rawCapacity);
    return { ownLb, heldLb, grossLb: ownLb + (fixedWeight ? 0 : heldLb), fixedWeight,
        capacityLb, remainingLb: capacityLb === null ? null : capacityLb - heldLb,
        lineGp, contentsGp, totalGp: lineGp + contentsGp };
}
export function inventoryOnHandTotals(rows: readonly InventoryDisplayRow[], creatureId: string, read = inventoryDisplayReaders()) {
    const onHand = inventoryOnHandRows(rows, creatureId, read);
    return { rows: onHand, weight: calculateInventoryWeight(onHand, read), gp: sumInventoryDescendantCostGp(onHand, read) };
}

/** Shared tree for both existing drill-down charts; links retain the instance identity. */
export function inventoryBreakdownNode(row: InventoryDisplayRow, metric: 'weight' | 'gp' = 'weight', read = inventoryDisplayReaders()): InventoryDisplayNode {
    const totals = inventoryRowTotals(row, read);
    const children = inventoryContents(row).map(child => inventoryBreakdownNode(child, metric, read));
    children.sort((a, b) => b[metric] - a[metric]);
    const node: InventoryDisplayNode = { name: String(read.inventoryField(row, 'inventory_name') ?? 'Item'),
        weight: Math.round(totals.grossLb * 100) / 100, gp: Math.round(totals.totalGp * 100) / 100, children };
    const id = String(read.inventoryField(row, 'inventory_id') ?? '');
    if (id) node.id = id;
    if (isInventoryDisplayContainer(row, read)) node.container = true;
    return node;
}
export function findInventoryDisplayRow(rows: readonly InventoryDisplayRow[], id: string, read = inventoryDisplayReaders()): InventoryDisplayRow | null {
    for (const row of rows) {
        if (String(read.inventoryField(row, 'inventory_id') ?? '').toUpperCase() === id.toUpperCase()) return row;
        const found = findInventoryDisplayRow(inventoryContents(row), id, read);
        if (found) return found;
    }
    return null;
}

/** Raw PHP cells are upgraded by a bundle owner, never by a PHP arithmetic fallback. */
export function registerInventoryTotalElement(compile: (value: unknown) => Promise<InventoryDisplayRow>): void {
    if (typeof customElements === 'undefined' || customElements.get('ft-inventory-total')) return;
    customElements.define('ft-inventory-total', class extends HTMLElement {
        connectedCallback(): void {
            queueMicrotask(() => {
                const input = this.querySelector('script')?.textContent;
                if (!input) return;
                void Promise.resolve().then(() => compile(JSON.parse(input))).then(async row => {
                    if (row._inventory_refusal === 'unported:inventory-instance-root') {
                        const owner = (globalThis as unknown as { ftResolveInventoryDisplayRow?: typeof compile }).ftResolveInventoryDisplayRow;
                        if (!owner) throw new Error('unported:inventory-display:instance-owner-unavailable');
                        row = await owner(row);
                    }
                    if (row._inventory_refusal || row._entity_refusal) throw new Error(String(row._inventory_refusal ?? row._entity_refusal));
                    if (!this.isConnected) return;
                    const key = this.dataset.kind as keyof ReturnType<typeof inventoryRowTotals>;
                    const value = inventoryRowTotals(row)[key];
                    if (value !== null && (typeof value !== 'number' || !Number.isFinite(value))) throw new Error('inventory-total:unknown-cell');
                    this.textContent = value === null ? '—' : key === 'lineGp' ? String(value)
                        : Number(value).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: Number.isInteger(value) ? 0 : 2 });
                    this.dispatchEvent(new CustomEvent('ft:inventory-total-ready', { bubbles: true }));
                }).catch(error => { this.textContent = String(error); this.dataset.ftError = '1'; });
            });
        }
    });
}
